import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { type ApiContainerAction, type ApiContainerType, EnumContainerActionStatus } from '@Model';
import { FrameEditContainerType } from '@Modules/Stammdaten';
import {
    type AsyncStateUpdate,
    LinkedField,
    useGuiBinder,
    useLinkedField,
    useLinkProperty,
    useTranslate,
} from '@synqo/frontend-component-library';

interface ContainerBilledAsCellProps {
    action: ApiContainerAction;
    /** Path to the container actions of the order; the row's own segment is appended here. */
    baseApiObjectPath: string;
    canEdit: boolean;
    requestUpdateState: AsyncStateUpdate<ApiContainerAction>;
}

/**
 * The container type one row is billed as. Only a planned row may change it.
 */
// @ai-generated 2026-10-28T13:39:59+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ContainerBilledAsCell({
    action,
    baseApiObjectPath,
    canEdit,
    requestUpdateState,
}: ContainerBilledAsCellProps) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const isPlanned = action.booking_status === EnumContainerActionStatus.Planned;

    const binder = useGuiBinder<ApiContainerAction>({
        data: action,
        meta: undefined,
        canEdit: canEdit && isPlanned,
        requestUpdateState,
    });
    const linkBilledAs = useLinkedField<ApiContainerAction, ApiContainerType>(
        `${baseApiObjectPath}/${action.id}`,
        'billed_as',
        FrameEditContainerType,
        hasPermission(Permission.StammdatenContainerTypeCreate),
        requestUpdateState,
    );
    const billedAsField = useLinkProperty(binder, linkBilledAs.linkApi);

    return (
        <LinkedField<ApiContainerType>
            {...billedAsField}
            {...linkBilledAs.events}
            label={i18n('Abgerechnet als')}
            placeholder={i18n('Containertyp wählen …')}
        />
    );
}
